import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions/placeholder'
import Callout from '../extensions/Callout'
import Embed from '../extensions/Embed'
import InlineImage from '../extensions/InlineImage'
import downscaleImage from '../support/downscaleImage'
import imageDataUrlToBlob from '../support/imageDataUrl'
import {
  copyExtensionData,
  createEditorExtensions,
  extensionCommandAllowlist,
  normalizeEditorExtensions,
} from '../support/editorExtensions'
import uploadTemporaryInlineImage, {
  deleteTemporaryInlineImage,
} from '../support/temporaryInlineImageUpload'

/**
 * Dispatched on the editor's root element, bubbling, whenever the user changes
 * the document. Carries no value: the bound form state is still written on blur.
 */
export const CHANGED_EVENT = 'resourcerer-rich-text-changed'
export const SYNC_EVENT = 'resourcerer-rich-text-sync'
export const SYNCED_EVENT = 'resourcerer-rich-text-synced'
export const UPDATE_EVENT = 'resourcerer-rich-text-update'
export const RECONCILE_INLINE_MEDIA_EVENT = 'resourcerer-rich-text-reconcile-inline-media'
export const FOCUS_EVENT = 'resourcerer-rich-text-focus'
export const EXTENSION_COMMAND_EVENT = 'resourcerer-rich-text-extension-command'
export const REMOUNT_REQUIRED_EVENT = 'resourcerer-rich-text-remount-required'

const LEGACY_FULL_FEATURES = [
  'paragraph',
  'hard-break',
  'undo-redo',
  'bold',
  'italic',
  'strike',
  'underline',
  'headings',
  'bullet-list',
  'ordered-list',
  'blockquote',
  'code',
  'link',
  'callouts',
  'horizontal-rule',
  'inline-images',
  'embeds',
]

const IMAGE_FILE_EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
}

const imageFileExtension = (mimeType) => IMAGE_FILE_EXTENSIONS[mimeType] || 'bin'

// Livewire's own PayloadTooLargeException is a generic 500 response. Its
// message is only present in the response body while application debug output
// is enabled, so this marker is a development backstop. The exact preflight is
// the production guard; status 413 covers web-server and proxy limits.
const PAYLOAD_TOO_LARGE_DEBUG_MARKER = 'Livewire request payload is too large'
const INLINE_MEDIA_PREVIEWS_UNCHANGED = Symbol('inline-media-previews-unchanged')
const INITIAL_DIALECT_UNSET = Symbol('initial-dialect-unset')
const handledPayloadRequests = new WeakSet()
const warnedPayloadComponentRoots = new WeakSet()
const expectedPayloadCancellationReasons = new WeakSet()
const payloadRequestsWithRemovedInlineImages = new WeakSet()

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (!expectedPayloadCancellationReasons.has(event.reason)) {
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
  }, true)
}

const textByteSize = (value) => new TextEncoder().encode(value).byteLength

const requestBodySize = (request) => {
  const body = request?.options?.body

  return typeof body === 'string' ? textByteSize(body) : null
}

const requestUpdatesBinding = (request, livewireComponentRoot, binding) => {
  if (!binding) {
    return false
  }

  return Array.from(request?.messages || []).some((message) => {
    if (message?.component?.el !== livewireComponentRoot) {
      return false
    }

    const updates = message?.payload?.updates || message?.updates

    if (!updates || typeof updates !== 'object') {
      return false
    }

    return Object.keys(updates).some((path) =>
      path === binding || path.startsWith(`${binding}.`) || binding.startsWith(`${path}.`)
    )
  })
}

const consumeExpectedActionRejections = (request) => {
  Array.from(request?.messages || []).forEach((message) => {
    Array.from(message?.actions || []).forEach((action) => {
      // Livewire rejects every action promise when request.cancel() runs.
      // Its async $commit() wrapper rejects with the same reason but is not
      // reachable from the request, so mark that exact object for the scoped
      // unhandledrejection listener above as well.
      action?.promise?.catch?.((reason) => {
        if (reason !== null && (typeof reason === 'object' || typeof reason === 'function')) {
          expectedPayloadCancellationReasons.add(reason)
        }
      })
    })
  })
}

const isPayloadTooLargeResponse = (response, body) =>
  response?.status === 413 ||
  (typeof body === 'string' && body.includes(PAYLOAD_TOO_LARGE_DEBUG_MARKER))

const INLINE_MEDIA_PREVIEWS_TYPE_ERROR =
  'Inline media previews must be a plain object mapping media IDs to non-empty URL strings.'
const TEMPORARY_UPLOAD_REFERENCES_TYPE_ERROR =
  'Temporary upload references must be a plain object mapping valid upload tokens to valid media UUIDs.'
const TEMPORARY_UPLOAD_TOKEN_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const INLINE_MEDIA_REFERENCE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isPlainRecord = (value) => {
  const prototype = value !== null && typeof value === 'object'
    ? Object.getPrototypeOf(value)
    : null

  return !Array.isArray(value)
    && value !== null
    && typeof value === 'object'
    && (prototype === Object.prototype || prototype === null)
}

export const normalizeInlineMediaPreviews = (previews) => {
  if (!isPlainRecord(previews)) {
    throw new TypeError(INLINE_MEDIA_PREVIEWS_TYPE_ERROR)
  }

  const normalized = new Map()

  Object.entries(previews).forEach(([reference, url]) => {
    if (reference.trim() === '' || typeof url !== 'string' || url.trim() === '') {
      throw new TypeError(INLINE_MEDIA_PREVIEWS_TYPE_ERROR)
    }

    normalized.set(reference.toLowerCase(), url)
  })

  return normalized
}

export const normalizeTemporaryUploadReferences = (references) => {
  if (!isPlainRecord(references)) {
    throw new TypeError(TEMPORARY_UPLOAD_REFERENCES_TYPE_ERROR)
  }

  const normalized = new Map()

  Object.entries(references).forEach(([token, reference]) => {
    if (!TEMPORARY_UPLOAD_TOKEN_PATTERN.test(token)
      || typeof reference !== 'string'
      || !INLINE_MEDIA_REFERENCE_PATTERN.test(reference)) {
      throw new TypeError(TEMPORARY_UPLOAD_REFERENCES_TYPE_ERROR)
    }

    normalized.set(token, reference.toLowerCase())
  })

  return normalized
}

const inlineMediaPreviewsEqual = (left, right) =>
  left.size === right.size
  && Array.from(left).every(([reference, url]) => right.get(reference) === url)

export default ({ settings, binding = null }) => {
  // The editor lives OUTSIDE the returned Alpine data object on purpose:
  // Alpine wraps data in a reactive proxy, and a proxied Editor breaks
  // ProseMirror's identity checks ("Applying a mismatched transaction").
  let editor = null
  // Server-confirmed value used exclusively as the safe 413 rollback point.
  let lastAcceptedValue = ''
  // Last value this instance read from or wrote to the morphable model input.
  // This is deliberately independent of Livewire's request interception.
  let lastWrittenInputValue = ''
  let suppressNextSync = false
  let syncSuppressionTimer = null
  let unsubscribePayloadGuard = null
  let unsubscribeMorphHook = null
  let refreshingUploadEndpoint = false
  let publicListeners = []
  let publicRoot = null
  let writingInput = false
  let uploadQueue = []
  let uploadDrainPromise = null
  let uploadRecords = new Map()
  let trackedTemporaryTokens = new Map()
  let removedTemporaryTokens = new Map()
  let activeUpload = null
  let uploadSequence = 0
  let resubmittingForm = false
  let uploadFailureWarned = false
  let alpineComponent = null
  let suppressTemporaryReconcile = false
  let placeholder = typeof settings.placeholder === 'string' ? settings.placeholder : ''
  const selectedExtensions = normalizeEditorExtensions(settings.extensions)
  let extensionCommands = new Map()
  let mountedDialect = settings.dialect ?? INITIAL_DIALECT_UNSET
  let dialectMarkerObserved = false
  let remountRequired = false

  const features = new Set(settings.features || LEGACY_FULL_FEATURES)
  const featureEnabled = (feature) => features.has(feature)
  const dialectIsCurrent = () => {
    const rendered = publicRoot?.dataset.richTextEditorDialect
    // Published legacy markup may never render a marker. Observation only arms the guard.
    if (rendered !== undefined) dialectMarkerObserved = true
    if (!remountRequired && publicRoot && dialectMarkerObserved && rendered !== mountedDialect) {
      remountRequired = true
      editor?.setEditable(false, false)
      if (alpineComponent) alpineComponent.isEditable = false
      publicRoot.dispatchEvent(new CustomEvent(REMOUNT_REQUIRED_EVENT, {
        bubbles: true, detail: { id: settings.id },
      }))
    }
    return !remountRequired
  }
  const initiallyEditable = settings.editable !== false
  const temporaryUploadsEnabled = typeof settings.inlineImages?.uploadUrl === 'string'
    && settings.inlineImages.uploadUrl !== ''
  const initialInlineMediaPreviews = settings.inlineMediaPreviews
  let inlineMediaPreviews = initialInlineMediaPreviews === undefined
    || (Array.isArray(initialInlineMediaPreviews) && initialInlineMediaPreviews.length === 0)
    ? new Map()
    : normalizeInlineMediaPreviews(initialInlineMediaPreviews)

  const hydrateInlineMediaPreviews = (html) => {
    if (!featureEnabled('inline-images')
      || typeof html !== 'string'
      || !html.toLowerCase().includes('data-rich-text-media')) {
      return html
    }

    const template = document.createElement('template')
    template.innerHTML = html

    Array.from(template.content.querySelectorAll('img[data-rich-text-media]')).forEach((image) => {
      const reference = image.getAttribute('data-rich-text-media')?.toLowerCase()
      const preview = reference ? inlineMediaPreviews.get(reference) : null

      // A persisted or externally supplied src is never trusted for a stable
      // reference. Only the server-authorized transient preview map may hydrate
      // the editor DOM; InlineImage.renderHTML removes it again on serialization.
      if (preview) {
        image.setAttribute('src', preview)
      } else {
        image.removeAttribute('src')
      }
    })

    return template.innerHTML
  }

  const reconcileTemporaryUploadReferencesInHtml = (html, references) => {
    if (typeof html !== 'string'
      || !html.toLowerCase().includes('data-rich-text-upload-token')) {
      return html
    }

    const template = document.createElement('template')
    template.innerHTML = html
    let changed = false

    Array.from(template.content.querySelectorAll('img[data-rich-text-upload-token]'))
      .forEach((image) => {
        const token = image.getAttribute('data-rich-text-upload-token')
        const reference = token ? references.get(token) : null

        if (!reference) {
          return
        }

        image.setAttribute('data-rich-text-media', reference)
        image.removeAttribute('src')
        image.removeAttribute('data-rich-text-upload-id')
        image.removeAttribute('data-rich-text-upload-state')
        image.removeAttribute('data-rich-text-upload-progress')
        image.removeAttribute('data-rich-text-upload-token')
        changed = true
      })

    return changed ? template.innerHTML : html
  }

  const refreshInlineMediaPreviews = () => {
    if (!editor || !featureEnabled('inline-images')) {
      return
    }

    const selection = editor.state.selection.getBookmark()
    const storedMarks = editor.state.storedMarks
    let transaction = editor.state.tr

    editor.state.doc.descendants((node, position) => {
      const reference = typeof node.attrs?.mediaReference === 'string'
        ? node.attrs.mediaReference.toLowerCase()
        : null

      if (node.type.name !== 'image' || reference === null) {
        return
      }

      const preview = inlineMediaPreviews.get(reference) || null

      if (node.attrs.src !== preview) {
        transaction = transaction.setNodeMarkup(position, undefined, {
          ...node.attrs,
          src: preview,
        })
      }
    })

    if (transaction.docChanged) {
      const preservedSelection = selection.resolve(transaction.doc)

      if (!transaction.selection.eq(preservedSelection)) {
        transaction.setSelection(preservedSelection)
      }

      if (storedMarks !== null) {
        transaction.setStoredMarks(storedMarks)
      }

      transaction.setMeta('preventUpdate', true)
      transaction.setMeta('addToHistory', false)
      editor.view.dispatch(transaction)
    }
  }

  const addPublicListener = (target, type, handler, options = false) => {
    target?.addEventListener(type, handler, options)

    if (target) {
      publicListeners.push({ target, type, handler, options })
    }
  }

  const detachPublicBindings = () => {
    publicListeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options)
    })
    publicListeners = []

    if (publicRoot?.resourcererRichText?.id === settings.id) {
      delete publicRoot.resourcererRichText
    }

    publicRoot = null
  }

  const extensions = () => {
    const listsEnabled = featureEnabled('bullet-list') || featureEnabled('ordered-list')
    const configured = [
      StarterKit.configure({
        bold: featureEnabled('bold') ? {} : false,
        italic: featureEnabled('italic') ? {} : false,
        strike: featureEnabled('strike') ? {} : false,
        underline: featureEnabled('underline') ? {} : false,
        heading: featureEnabled('headings') ? { levels: [2, 3, 4] } : false,
        bulletList: featureEnabled('bullet-list') ? {} : false,
        orderedList: featureEnabled('ordered-list') ? {} : false,
        listItem: listsEnabled ? {} : false,
        listKeymap: listsEnabled ? {} : false,
        blockquote: featureEnabled('blockquote') ? {} : false,
        code: featureEnabled('code') ? {} : false,
        codeBlock: featureEnabled('code') ? {} : false,
        link: featureEnabled('link') ? {} : false,
        horizontalRule: featureEnabled('horizontal-rule') ? {} : false,
      }),
      Placeholder.configure({
        placeholder: () => placeholder,
        showOnlyWhenEditable: false,
      }),
    ]

    if (featureEnabled('inline-images')) {
      configured.push(InlineImage.configure({
        allowBase64: true,
        uploadMessages: settings.messages,
      }))
    }

    if (featureEnabled('callouts')) {
      configured.push(Callout)
    }

    if (featureEnabled('embeds')) {
      configured.push(Embed)
    }

    return [...configured, ...createEditorExtensions(selectedExtensions, features)]
  }

  const armSyncSuppression = () => {
    suppressNextSync = true
    clearTimeout(syncSuppressionTimer)
    syncSuppressionTimer = setTimeout(() => {
      suppressNextSync = false
      syncSuppressionTimer = null
    }, 0)
  }

  const consumeSyncSuppression = () => {
    if (!suppressNextSync) {
      return false
    }

    suppressNextSync = false
    clearTimeout(syncSuppressionTimer)
    syncSuppressionTimer = null

    return true
  }

  const warnPayloadTooLarge = (livewireComponentRoot, removedInlineImages = false) => {
    if (warnedPayloadComponentRoots.has(livewireComponentRoot)) {
      return
    }

    warnedPayloadComponentRoots.add(livewireComponentRoot)
    window.alert(
      removedInlineImages ? settings.messages.imageRejected : settings.messages.payloadTooLarge
    )
  }

  const cannotFitPayloadByItself = (dataUrl) =>
    typeof settings.livewirePayloadMaxSize === 'number' &&
    textByteSize(dataUrl) >= settings.livewirePayloadMaxSize

  const createObjectUrl = (value, id) => typeof window.URL.createObjectURL === 'function'
    ? window.URL.createObjectURL(value)
    : `blob:resourcerer-rich-text/${id}`

  const revokeObjectUrl = (url) => {
    if (typeof url === 'string' && url.startsWith('blob:') && typeof window.URL.revokeObjectURL === 'function') {
      window.URL.revokeObjectURL(url)
    }
  }

  const updateUploadNode = (uploadId, attributes) => {
    if (!editor) {
      return false
    }

    let transaction = editor.state.tr
    let found = false

    editor.state.doc.descendants((node, position) => {
      if (node.type.name !== 'image' || node.attrs.uploadId !== uploadId) {
        return
      }

      found = true
      transaction = transaction.setNodeMarkup(position, undefined, {
        ...node.attrs,
        ...attributes,
      })
    })

    if (!found) {
      return false
    }

    editor.view.dispatch(
      transaction
        .setMeta('preventUpdate', true)
        .setMeta('addToHistory', false)
    )

    return true
  }

  const documentUploadState = () => {
    const uploadIds = new Set()
    const tokens = new Map()
    let pending = false
    let failed = false

    editor?.state.doc.descendants((node) => {
      if (node.type.name !== 'image') {
        return
      }

      if (typeof node.attrs.uploadId === 'string') {
        uploadIds.add(node.attrs.uploadId)
      }

      if (typeof node.attrs.uploadToken === 'string') {
        tokens.set(node.attrs.uploadToken, node.attrs.src)
      }

      pending = pending || ['processing', 'uploading'].includes(node.attrs.uploadState)
      failed = failed || node.attrs.uploadState === 'error'
    })

    return { uploadIds, tokens, pending, failed }
  }

  const deleteTemporaryToken = (token, url) => {
    trackedTemporaryTokens.delete(token)
    removedTemporaryTokens.delete(token)
    void deleteTemporaryInlineImage(url, settings.inlineImages?.csrfToken).catch(() => {
      // The server-side expiry/pruner remains the safety net when a best-effort
      // removal request is interrupted or the browser goes offline.
    })
  }

  const flushRemovedTemporaryTokens = () => {
    const removals = Array.from(removedTemporaryTokens.entries())
    removedTemporaryTokens = new Map()

    removals.forEach(([token, url]) => deleteTemporaryToken(token, url))
  }

  const isTemporaryPreviewUrl = (url) => {
    if (typeof url !== 'string') {
      return false
    }

    try {
      const preview = new window.URL(url, window.location.href)
      const upload = new window.URL(settings.inlineImages.uploadUrl, window.location.href)

      return preview.origin === upload.origin
        && preview.pathname.startsWith(`${upload.pathname.replace(/\/$/, '')}/`)
    } catch {
      return false
    }
  }

  const reconcileTemporaryImages = () => {
    if (!temporaryUploadsEnabled || !editor) {
      return
    }

    const { uploadIds, tokens } = documentUploadState()

    tokens.forEach((url, token) => {
      if (removedTemporaryTokens.has(token)) {
        const deleteUrl = removedTemporaryTokens.get(token)

        removedTemporaryTokens.delete(token)
        trackedTemporaryTokens.set(token, deleteUrl)

        return
      }

      if (!trackedTemporaryTokens.has(token) && isTemporaryPreviewUrl(url)) {
        trackedTemporaryTokens.set(token, url)
      }
    })

    uploadRecords.forEach((record, uploadId) => {
      if (uploadIds.has(uploadId)) {
        return
      }

      record.removed = true
      revokeObjectUrl(record.objectUrl)

      if (activeUpload?.id === uploadId) {
        activeUpload.controller.abort()
      }

      uploadRecords.delete(uploadId)
    })

    trackedTemporaryTokens.forEach((url, token) => {
      if (!tokens.has(token)) {
        // Keep the owner-bound file available for redo. Explicitly removed
        // tokens are deleted when this editor instance is torn down; expiry
        // remains the safety net if teardown/navigation interrupts the call.
        trackedTemporaryTokens.delete(token)
        removedTemporaryTokens.set(token, url)
      }
    })
  }

  const showUploadFailure = () => {
    window.alert(settings.messages.imageUploadFailed || settings.messages.imageRejected)
  }

  const warnUploadFailure = () => {
    if (uploadFailureWarned) {
      return
    }

    uploadFailureWarned = true
    showUploadFailure()
  }

  const outputFileName = (file, blob) => {
    const extension = imageFileExtension(blob.type)
    const baseName = typeof file.name === 'string' && file.name !== ''
      ? file.name.replace(/\.[^.]*$/, '')
      : 'inline-image'

    return `${baseName}.${extension}`
  }

  const createUploadRecord = (file) => {
    uploadSequence += 1

    const id = `${settings.id || 'rich-text'}-upload-${uploadSequence}`

    return {
      id,
      file,
      objectUrl: createObjectUrl(file, id),
      progress: null,
      removed: false,
      state: 'processing',
    }
  }

  const discardUploadRecords = (records) => {
    records.forEach((record) => {
      record.removed = true
      revokeObjectUrl(record.objectUrl)
      uploadRecords.delete(record.id)
    })
  }

  const embeddedImageContent = (dataTransfer) => {
    if (!temporaryUploadsEnabled || typeof dataTransfer?.getData !== 'function') {
      return null
    }

    const html = dataTransfer.getData('text/html')

    if (typeof html !== 'string' || !html.includes('data:image/')) {
      return null
    }

    const template = document.createElement('template')
    template.innerHTML = html
    const records = []
    let found = false
    let rejected = false

    Array.from(template.content.querySelectorAll('img')).forEach((image, index) => {
      const source = image.getAttribute('src')

      if (typeof source !== 'string' || !source.startsWith('data:image/')) {
        return
      }

      found = true

      const blob = imageDataUrlToBlob(source)

      if (blob === null) {
        image.remove()
        rejected = true

        return
      }

      const extension = imageFileExtension(blob.type)
      const file = new window.File(
        [blob],
        `pasted-inline-image-${index + 1}.${extension}`,
        { type: blob.type },
      )
      const record = createUploadRecord(file)

      records.push(record)
      image.setAttribute('src', record.objectUrl)
      image.setAttribute('data-rich-text-upload-id', record.id)
      image.setAttribute('data-rich-text-upload-state', 'processing')
      image.removeAttribute('data-rich-text-upload-progress')
      image.removeAttribute('data-rich-text-upload-token')
    })

    return found
      ? { html: template.innerHTML, records, rejected }
      : null
  }

  const failUploadRecord = (record) => {
    record.state = 'error'
    updateUploadNode(record.id, {
      uploadState: 'error',
      uploadProgress: null,
    })
    warnUploadFailure()
  }

  const refreshUploadEndpointFromDom = () => {
    const configuration = alpineComponent?.$el?.querySelector('[data-rich-text-initial-settings]')

    if (!temporaryUploadsEnabled || !configuration) {
      return false
    }

    try {
      const endpoints = JSON.parse(configuration.textContent)?.inlineImages

      if (!isPlainRecord(endpoints) || typeof endpoints.uploadUrl !== 'string' || endpoints.uploadUrl === '') {
        return false
      }

      // Compact roots survive morphs. Only these server-issued request settings
      // change; keep the editor's profile, document and upload runtime intact.
      settings.inlineImages.uploadUrl = endpoints.uploadUrl
      if (typeof endpoints.csrfToken === 'string' || endpoints.csrfToken === null) {
        settings.inlineImages.csrfToken = endpoints.csrfToken
      }

      return true
    } catch {
      return false
    }
  }

  const refreshExpiredUploadEndpoint = async () => {
    const root = alpineComponent?.$el

    if (!root?.querySelector('[data-rich-text-initial-settings]') || typeof window.Livewire?.find !== 'function') {
      return false
    }

    const componentRoot = root.closest('[wire\\:id]')
    const componentId = componentRoot?.getAttribute('wire:id')
    const wire = componentId ? window.Livewire.find(componentId) : null

    if (!wire || wire.$el !== componentRoot || typeof wire.$refresh !== 'function') {
      return false
    }

    // Use Livewire's registered component and built-in refresh action. Its
    // response supplies fresh signed settings without replacing the local draft.
    refreshingUploadEndpoint = true
    try {
      await wire.$refresh()
    } finally {
      refreshingUploadEndpoint = false
    }

    return editor !== null && alpineComponent?.$el === root && componentRoot.contains(root)
      && refreshUploadEndpointFromDom()
  }

  const processUploadRecord = async (record) => {
    if (record.removed || !uploadRecords.has(record.id)) {
      return
    }

    const dataUrl = await downscaleImage(record.file, settings.inlineImages)

    if (record.removed || !uploadRecords.has(record.id)) {
      return
    }

    const blob = imageDataUrlToBlob(dataUrl)

    if (dataUrl === null || blob === null) {
      failUploadRecord(record)

      return
    }

    const processedObjectUrl = createObjectUrl(blob, record.id)
    revokeObjectUrl(record.objectUrl)
    record.objectUrl = processedObjectUrl
    record.state = 'uploading'
    updateUploadNode(record.id, {
      src: processedObjectUrl,
      uploadState: 'uploading',
      uploadProgress: 0,
    })

    const controller = new window.AbortController()
    activeUpload = { id: record.id, controller }

    try {
      const uploadImage = () => uploadTemporaryInlineImage(blob, {
        url: settings.inlineImages.uploadUrl,
        fileName: outputFileName(record.file, blob),
        csrfToken: settings.inlineImages.csrfToken,
        signal: controller.signal,
        onProgress: (progress) => {
          if (record.removed || record.progress === progress) {
            return
          }

          record.progress = progress
          updateUploadNode(record.id, { uploadProgress: progress })
        },
      })
      let upload

      try {
        upload = await uploadImage()
      } catch (error) {
        // Laravel's signed middleware identifies expired/invalid signatures with
        // this 403. Authorization/validation/network failures do not refresh.
        if (error?.status !== 403 || error?.message !== 'Invalid signature.'
          || controller.signal.aborted || record.removed || !uploadRecords.has(record.id)
          || !await refreshExpiredUploadEndpoint()) {
          throw error
        }

        if (controller.signal.aborted || record.removed || !uploadRecords.has(record.id)) {
          return
        }

        // One retry, inside the same serial queue slot, reuses the processed
        // blob and abort controller. A second 403 remains a failed placeholder.
        upload = await uploadImage()
      }

      if (record.removed || !updateUploadNode(record.id, {
        src: upload.previewUrl,
        uploadId: null,
        uploadState: null,
        uploadProgress: null,
        uploadToken: upload.token,
      })) {
        deleteTemporaryToken(upload.token, upload.deleteUrl)

        return
      }

      trackedTemporaryTokens.set(upload.token, upload.deleteUrl)
      uploadRecords.delete(record.id)
      revokeObjectUrl(record.objectUrl)
      record.objectUrl = null
      alpineComponent?.sync()
    } catch (error) {
      if (!record.removed && error?.name !== 'AbortError') {
        failUploadRecord(record)
      }
    } finally {
      if (activeUpload?.id === record.id) {
        activeUpload = null
      }

      reconcileTemporaryImages()
    }
  }

  const drainUploadQueue = () => {
    if (uploadDrainPromise !== null) {
      return uploadDrainPromise
    }

    const promise = (async () => {
      while (uploadQueue.length > 0) {
        const record = uploadQueue.shift()

        if (record) {
          try {
            await processUploadRecord(record)
          } catch {
            if (!record.removed && uploadRecords.has(record.id)) {
              failUploadRecord(record)
            }
          }
        }
      }
    })().finally(() => {
      if (uploadDrainPromise !== promise) {
        return
      }

      uploadDrainPromise = null

      if (uploadQueue.length > 0) {
        drainUploadQueue()
      }
    })

    uploadDrainPromise = promise

    return uploadDrainPromise
  }

  const waitForUploads = async () => {
    while (uploadDrainPromise !== null || uploadQueue.length > 0) {
      await (uploadDrainPromise || drainUploadQueue())
    }
  }

  const resetUploadRuntime = () => {
    activeUpload?.controller.abort()
    activeUpload = null
    uploadQueue = []
    uploadRecords.forEach((record) => {
      record.removed = true
      revokeObjectUrl(record.objectUrl)
    })
    uploadRecords = new Map()
    trackedTemporaryTokens = new Map()
    removedTemporaryTokens = new Map()
    resubmittingForm = false
    uploadFailureWarned = false
  }

  return {
    isEditable: initiallyEditable,

    active: {
      bold: false,
      italic: false,
      strike: false,
      heading2: false,
      heading3: false,
      bulletList: false,
      orderedList: false,
      blockquote: false,
      calloutInfo: false,
      calloutTip: false,
      calloutWarning: false,
      link: false,
      underline: false,
      heading4: false,
      code: false,
      codeBlock: false,
    },

    init() {
      // Alpine can call init() again around a Livewire remount. Tear down the
      // previous closure-owned instance first so one DOM node never has two
      // ProseMirror views attached to it.
      detachPublicBindings()
      flushRemovedTemporaryTokens()
      resetUploadRuntime()
      editor?.destroy()
      editor = null
      unsubscribePayloadGuard?.()
      unsubscribePayloadGuard = null
      unsubscribeMorphHook?.()
      unsubscribeMorphHook = null
      alpineComponent = this
      extensionCommands = new Map()
      // Capture the initial markup fallback once; re-init must not learn a replacement dialect.
      if (mountedDialect === INITIAL_DIALECT_UNSET) {
        mountedDialect = this.$el?.dataset.richTextEditorDialect
      }

      const renderedEditable = this.$el?.dataset.richTextEditorEditable
      this.isEditable = renderedEditable === 'true'
        ? true
        : renderedEditable === 'false'
          ? false
          : initiallyEditable
      if (remountRequired) this.isEditable = false

      lastAcceptedValue = this.$refs.input?.value ?? ''
      lastWrittenInputValue = lastAcceptedValue
      editor = new Editor({
        element: this.$refs.editor,
        extensions: extensions(),
        content: hydrateInlineMediaPreviews(this.$refs.input.value || ''),
        editable: this.isEditable,
        onBeforeCreate: ({ editor: instance }) => {
          if (selectedExtensions.length) {
            extensionCommands = extensionCommandAllowlist(instance, selectedExtensions)
          }
        },
        editorProps: {
          attributes: settings.editorAttributes || {},
          handlePaste: (view, event) => this.handlePaste(event),
          handleDrop: (view, event, slice, moved) => {
            if (moved) {
              return false
            }

            // Insert at the drop coordinates - not at the current selection,
            // which initially sits at the top of the document.
            const dropPosition = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null

            return this.handleDrop(event, dropPosition)
          },
        },
        onTransaction: () => {
          this.refreshActive()

          if (temporaryUploadsEnabled && !suppressTemporaryReconcile) {
            queueMicrotask(reconcileTemporaryImages)
          }
        },
        onBlur: () => this.sync(),
        // A signal, not a sync: the bound value still travels on blur, so a
        // long document is not pushed through livewire on every keystroke.
        // Hosts that need to know the editor is dirty before the user leaves
        // it - to enable a save button, say - listen for this instead of
        // watching the form state.
        onUpdate: () => {
          this.notifyChanged()
        },
      })

      publicRoot = this.$el
      this.registerPayloadGuard()
      this.registerMorphSync()
      this.refreshEditorOptionsFromDom()
      this.refreshActive()
      reconcileTemporaryImages()

      addPublicListener(this.$refs.input, 'input', (event) => this.handleInputEvent(event))
      addPublicListener(this.$el?.closest('form'), 'submit', (event) => this.handleFormSubmit(event), true)

      if (typeof settings.id === 'string' && settings.id !== '') {
        addPublicListener(window, SYNC_EVENT, (event) => this.handleSyncRequest(event))
        addPublicListener(window, UPDATE_EVENT, (event) => this.handleExternalUpdateRequest(event))
        addPublicListener(
          window,
          RECONCILE_INLINE_MEDIA_EVENT,
          (event) => this.handleInlineMediaReconciliationRequest(event),
        )
        addPublicListener(window, FOCUS_EVENT, (event) => this.handleFocusRequest(event))
        addPublicListener(window, EXTENSION_COMMAND_EVENT, (event) => {
          if (event.detail?.id === settings.id) {
            const { extension, command, args = [] } = event.detail
            if (!Array.isArray(args)) throw new TypeError('RichText extension command arguments must be a list.')
            this.extensionCommand(extension, command, ...args)
          }
        })
      }

      if (this.$el) {
        const mountedEditor = editor
        const handle = Object.freeze({
          id: settings.id,
          sync: () => this.sync(),
          whenReady: () => this.whenReady(),
          update: (value, previews) => this.externalUpdate(value, previews),
          reconcileInlineMedia: (references, previews) =>
            this.reconcileInlineMedia(references, previews),
          focus: () => this.focus(),
          extensionCommand: (extension, command, ...args) => editor === mountedEditor
            && this.extensionCommand(extension, command, ...args),
        })

        publicRoot = this.$el
        publicRoot.resourcererRichText = handle
      }
    },

    /**
     * Bubbles out of the editor so a listener anywhere above it can react.
     * Fires for user edits only: programmatic replacements go through
     * externalUpdate(), which suppresses tiptap's update event.
     */
    notifyChanged() {
      // From the editor element, not $el: it always exists, and the event
      // bubbles to whatever wrapper is listening either way.
      this.$refs.editor?.dispatchEvent(
        new CustomEvent(CHANGED_EVENT, {
          bubbles: true,
          detail: { id: settings.id },
        })
      )
    },

    destroy() {
      unsubscribePayloadGuard?.()
      unsubscribePayloadGuard = null
      unsubscribeMorphHook?.()
      unsubscribeMorphHook = null
      clearTimeout(syncSuppressionTimer)
      syncSuppressionTimer = null
      suppressNextSync = false

      detachPublicBindings()
      flushRemovedTemporaryTokens()
      resetUploadRuntime()

      editor?.destroy()
      editor = null
      alpineComponent = null
    },

    registerMorphSync() {
      if (typeof window.Livewire?.hook !== 'function') {
        return
      }

      const root = this.$el

      unsubscribeMorphHook = window.Livewire.hook('morph.updated', ({ el }) => {
        const input = this.$refs.input

        if (!root || !input || !el) {
          return
        }

        if (el !== root && el !== input && !root.contains(el) && !el.contains?.(root)) {
          return
        }

        const endpointsOnly = refreshingUploadEndpoint

        queueMicrotask(() => {
          this.refreshEditorOptionsFromDom()

          if (endpointsOnly) {
            if (this.$refs.input) this.$refs.input.value = lastWrittenInputValue

            return
          }

          const nextValue = this.$refs.input?.value ?? ''

          // An unrelated Livewire roundtrip can morph any ancestor while this
          // editor still has an unsynced draft. Only treat the input as an
          // authoritative replacement when Livewire actually changed it since
          // this editor last read or wrote it. Server acceptance is tracked
          // separately for payload rollback.
          if (nextValue === lastWrittenInputValue) {
            return
          }

          this.externalUpdate(nextValue)
        })
      })
    },

    refreshEditorOptionsFromDom() {
      if (!editor || !this.$el || !dialectIsCurrent()) {
        return
      }

      refreshUploadEndpointFromDom()

      const editableAttribute = this.$el.dataset.richTextEditorEditable

      if (editableAttribute === 'true' || editableAttribute === 'false') {
        const nextEditable = editableAttribute === 'true'

        if (nextEditable !== this.isEditable) {
          this.isEditable = nextEditable
          editor.setEditable(nextEditable, false)
        }
      }

      const serializedAttributes = this.$el.dataset.richTextEditorAttributes

      if (typeof serializedAttributes !== 'string') {
        return
      }

      try {
        const attributes = JSON.parse(serializedAttributes)

        if (attributes === null || Array.isArray(attributes) || typeof attributes !== 'object') {
          return
        }

        placeholder = typeof attributes['data-placeholder'] === 'string'
          ? attributes['data-placeholder']
          : ''

        editor.setOptions({
          editorProps: {
            ...editor.options.editorProps,
            attributes,
          },
        })
      } catch {
        // Server-rendered attributes are valid JSON. Ignore a consumer-side
        // mutation instead of breaking editor sync during a Livewire morph.
      }
    },

    registerPayloadGuard() {
      // $wire is a fallback proxy: on older Livewire versions any unknown
      // property looks like a callable server method. Feature-detect against
      // the real global API before touching the component proxy.
      if (typeof window.Livewire?.interceptRequest !== 'function' || !this.$wire) {
        return
      }

      unsubscribePayloadGuard?.()

      const livewireComponentRoot = this.$wire.$el
      const input = this.$refs.input
      const removeRejectedInlineImages = (submittedValue) => {
        if (!editor || typeof submittedValue !== 'string') {
          return false
        }

        const ranges = []

        editor.state.doc.descendants((node, position) => {
          const source = node.type.name === 'image' ? node.attrs.src : null

          if (
            typeof source !== 'string' ||
            !source.startsWith('data:image/') ||
            !submittedValue.includes(source) ||
            lastAcceptedValue.includes(source)
          ) {
            return
          }

          ranges.push({ from: position, to: position + node.nodeSize })
        })

        if (ranges.length === 0) {
          return false
        }

        const transaction = ranges
          .reverse()
          .reduce((current, range) => current.delete(range.from, range.to), editor.state.tr)
          .setMeta('preventUpdate', true)

        editor.view.dispatch(transaction)

        return true
      }

      const recoverRejectedValue = (submittedValue) => {
        if (!binding || !input) {
          return false
        }

        // window.alert() normally moves focus away from Tiptap and fires
        // onBlur in the same task. Ignore only that immediate echo, or it would
        // put a rejected value straight back into Livewire's ephemeral state.
        // If a browser delays blur beyond the zero-timeout reset, one more
        // oversized roundtrip is cancelled; episode dedup prevents a loop and
        // another alert.
        armSyncSuppression()

        const removedInlineImages = removeRejectedInlineImages(submittedValue)
        const currentValue = editor?.isEmpty ? '' : editor?.getHTML() ?? lastAcceptedValue
        let recoveredValue = currentValue

        // Without a removable new image, keep the visible editor and Livewire
        // aligned. During the exact preflight they still equal submittedValue,
        // so reset both to the last accepted state instead of reporting a false
        // successful save. A late 413 may arrive after the user has edited the
        // document again; preserve that newer value for the next exact check.
        if (!removedInlineImages && currentValue === submittedValue) {
          recoveredValue = lastAcceptedValue
          editor?.commands.setContent(hydrateInlineMediaPreviews(recoveredValue), { emitUpdate: false })
        }

        input.value = recoveredValue
        lastWrittenInputValue = recoveredValue
        this.$wire.$set(binding, recoveredValue, false)

        return removedInlineImages
      }

      const unsubscribe = this.$wire.interceptRequest(({ request, onError, onSend, onSuccess }) => {
        const updatesBinding = requestUpdatesBinding(request, livewireComponentRoot, binding)
        const submittedValue = updatesBinding ? input?.value ?? '' : null

        onSuccess(() => {
          warnedPayloadComponentRoots.delete(livewireComponentRoot)

          if (updatesBinding) {
            lastAcceptedValue = submittedValue
          }
        })

        onError(({ response, body, preventDefault }) => {
          if (!isPayloadTooLargeResponse(response, body)) {
            return
          }

          consumeExpectedActionRejections(request)

          if (updatesBinding && recoverRejectedValue(submittedValue)) {
            payloadRequestsWithRemovedInlineImages.add(request)
          }

          if (handledPayloadRequests.has(request)) {
            return
          }

          handledPayloadRequests.add(request)
          preventDefault()
          queueMicrotask(() => warnPayloadTooLarge(
            livewireComponentRoot,
            payloadRequestsWithRemovedInlineImages.has(request)
          ))
        })

        const configuredMaximumSize = settings.livewirePayloadMaxSize
        const bodySize = requestBodySize(request)

        if (typeof configuredMaximumSize !== 'number' || bodySize === null) {
          return
        }

        if (bodySize <= configuredMaximumSize) {
          return
        }

        consumeExpectedActionRejections(request)

        if (updatesBinding && recoverRejectedValue(submittedValue)) {
          payloadRequestsWithRemovedInlineImages.add(request)
        }

        if (handledPayloadRequests.has(request)) {
          return
        }

        handledPayloadRequests.add(request)
        // Livewire builds request interceptors before message interceptors. A
        // synchronous cancel here would run before the message onFinish hooks
        // exist, then leave that cancelled message in the global active-message
        // set forever. Wait until onSend, then one microtask longer so every
        // message onSend hook has started its loading state before cancel()
        // runs the matching finish hooks and immediately aborts the fetch.
        onSend(() => queueMicrotask(() => {
          request.cancel()
          warnPayloadTooLarge(
            livewireComponentRoot,
            payloadRequestsWithRemovedInlineImages.has(request)
          )
        }))
      })

      unsubscribePayloadGuard = typeof unsubscribe === 'function' ? unsubscribe : null
    },

    getEditor() {
      return editor
    },

    // Applies the server-confirmed token-to-media mapping to the current
    // document without replacing newer user text. Only matching temporary
    // image nodes are changed, in one transaction outside the undo history.
    reconcileInlineMedia(
      references,
      previews = INLINE_MEDIA_PREVIEWS_UNCHANGED,
    ) {
      if (!editor || !dialectIsCurrent()) {
        return
      }

      // Both public records are normalized in full before any closure, input
      // or ProseMirror state changes. A bad later entry therefore cannot leave
      // an earlier token half-reconciled.
      const normalizedReferences = normalizeTemporaryUploadReferences(references)
      const normalizedPreviews = previews === INLINE_MEDIA_PREVIEWS_UNCHANGED
        ? INLINE_MEDIA_PREVIEWS_UNCHANGED
        : normalizeInlineMediaPreviews(previews)

      if (normalizedReferences.size === 0) {
        return
      }

      const selection = editor.state.selection.getBookmark()
      const storedMarks = editor.state.storedMarks
      const reconciledTokens = new Set()
      let transaction = editor.state.tr

      editor.state.doc.descendants((node, position) => {
        const token = node.type.name === 'image' && typeof node.attrs?.uploadToken === 'string'
          ? node.attrs.uploadToken
          : null
        const reference = token ? normalizedReferences.get(token) : null

        if (!token || !reference) {
          return
        }

        const preview = normalizedPreviews === INLINE_MEDIA_PREVIEWS_UNCHANGED
          ? inlineMediaPreviews.get(reference) || null
          : normalizedPreviews.get(reference) || null

        transaction = transaction.setNodeMarkup(position, undefined, {
          ...node.attrs,
          src: preview,
          uploadId: null,
          uploadState: null,
          uploadProgress: null,
          uploadToken: null,
          mediaReference: reference,
        })
        reconciledTokens.add(token)
      })

      if (!transaction.docChanged) {
        return
      }

      const preservedSelection = selection.resolve(transaction.doc)

      if (!transaction.selection.eq(preservedSelection)) {
        transaction.setSelection(preservedSelection)
      }

      if (storedMarks !== null) {
        transaction.setStoredMarks(storedMarks)
      }

      transaction.setMeta('preventUpdate', true)
      transaction.setMeta('addToHistory', false)

      const previousTemporaryReconcileSuppression = suppressTemporaryReconcile
      suppressTemporaryReconcile = true

      try {
        editor.view.dispatch(transaction)
      } finally {
        suppressTemporaryReconcile = previousTemporaryReconcileSuppression
      }

      if (normalizedPreviews !== INLINE_MEDIA_PREVIEWS_UNCHANGED) {
        // This is the same authoritative replacement contract as an external
        // update. Reconciliation only rewrites matching temporary nodes; the
        // full map is retained for subsequent preview hydration.
        inlineMediaPreviews = normalizedPreviews
      }

      reconciledTokens.forEach((token) => {
        // The server already consumed this owner-bound upload. Do not enqueue
        // its old delete URL during teardown as though the user removed it.
        trackedTemporaryTokens.delete(token)
        removedTemporaryTokens.delete(token)
      })

      lastAcceptedValue = reconcileTemporaryUploadReferencesInHtml(
        lastAcceptedValue,
        normalizedReferences,
      )

      const currentValue = editor.isEmpty ? '' : editor.getHTML()
      lastWrittenInputValue = currentValue

      if (this.$refs.input) {
        // Deliberately no native input or synced event: the server confirmation
        // must not create another user-change/autosave cycle.
        this.$refs.input.value = currentValue
      }

      this.refreshActive()
    },

    // Applies form-state changes that happen outside the editor (the editor
    // sits in wire:ignore) - e.g. the translateable locale switch replacing
    // the bound value. The equality guard swallows the echo of our own
    // sync() so typing never resets the cursor.
    externalUpdate(value, previews = INLINE_MEDIA_PREVIEWS_UNCHANGED) {
      if (!editor || !dialectIsCurrent()) {
        return
      }

      const normalizedPreviews = previews === INLINE_MEDIA_PREVIEWS_UNCHANGED
        ? INLINE_MEDIA_PREVIEWS_UNCHANGED
        : normalizeInlineMediaPreviews(previews)
      const nextValue = typeof value === 'string' ? value : ''
      const current = editor.isEmpty ? '' : editor.getHTML()
      const replacesInlineMediaPreviews = normalizedPreviews !== INLINE_MEDIA_PREVIEWS_UNCHANGED
      const changesInlineMediaPreviews = replacesInlineMediaPreviews
        && !inlineMediaPreviewsEqual(inlineMediaPreviews, normalizedPreviews)

      if (changesInlineMediaPreviews) {
        inlineMediaPreviews = normalizedPreviews
      }

      lastAcceptedValue = nextValue
      lastWrittenInputValue = nextValue

      if (this.$refs.input && this.$refs.input.value !== nextValue) {
        this.$refs.input.value = nextValue
      }

      if (nextValue === current) {
        if (changesInlineMediaPreviews) {
          refreshInlineMediaPreviews()
        }

        return
      }

      // emitUpdate defaults to true: without this, swapping the content from
      // outside would announce itself as a user edit.
      if (temporaryUploadsEnabled) {
        // An authoritative replacement can be a translateable locale switch.
        // Tokens absent from the newly visible locale may still be referenced
        // by another bound locale value, so they must expire/be claimed there,
        // not be treated as an explicit user deletion in this document.
        trackedTemporaryTokens = new Map()
        suppressTemporaryReconcile = true
      }

      try {
        editor.commands.setContent(hydrateInlineMediaPreviews(nextValue), { emitUpdate: false })
      } finally {
        suppressTemporaryReconcile = false
      }

      reconcileTemporaryImages()
      this.refreshActive()
    },

    handleInputEvent(event) {
      if (writingInput || event.target !== this.$refs.input) {
        return
      }

      this.externalUpdate(event.target.value)
    },

    handleFormSubmit(event) {
      if (!dialectIsCurrent()) {
        event.preventDefault()
        event.stopImmediatePropagation()
        return
      }

      if (!temporaryUploadsEnabled) {
        this.sync()

        return
      }

      if (resubmittingForm) {
        resubmittingForm = false
        this.sync()

        return
      }

      const state = documentUploadState()
      const pending = state.pending || uploadDrainPromise !== null || uploadQueue.length > 0

      if (!pending && !state.failed) {
        this.sync()

        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()

      if (state.failed) {
        // Failure-time feedback is intentionally deduplicated, but every
        // blocked save attempt must explain why nothing happened.
        showUploadFailure()

        return
      }

      const form = event.currentTarget
      const submitter = event.submitter

      void (async () => {
        if (!await this.whenReady()) {
          return
        }

        resubmittingForm = true

        try {
          if (submitter instanceof window.HTMLElement) {
            form.requestSubmit(submitter)
          } else {
            form.requestSubmit()
          }
        } catch {
          // A detached/invalid submitter may make requestSubmit() throw. The
          // original user event is already stopped; leave the form untouched.
        } finally {
          // requestSubmit() performs interactive validation first. An invalid
          // form dispatches no submit event and throws no exception, so never
          // rely on the resubmit branch alone to release this guard.
          queueMicrotask(() => {
            resubmittingForm = false
          })
        }
      })()
    },

    /**
     * Await pending temporary uploads, reject persistent error placeholders,
     * and only then flush compact token HTML into the bound input. Non-submit
     * save/autosave integrations should await this method before persisting.
     */
    async whenReady() {
      if (!editor || !dialectIsCurrent()) {
        return false
      }

      if (temporaryUploadsEnabled) {
        await waitForUploads()

        if (!editor || !dialectIsCurrent()) {
          return false
        }

        const state = documentUploadState()

        if (state.pending || state.failed) {
          showUploadFailure()

          return false
        }
      }

      this.sync()

      return true
    },

    handleSyncRequest(event) {
      if (event.detail?.id !== settings.id) {
        return
      }

      this.sync()
    },

    handleExternalUpdateRequest(event) {
      if (event.detail?.id !== settings.id) {
        return
      }

      this.externalUpdate(event.detail?.value, event.detail?.inlineMediaPreviews)
    },

    handleInlineMediaReconciliationRequest(event) {
      if (event.detail?.id !== settings.id) {
        return
      }

      this.reconcileInlineMedia(
        event.detail?.temporaryUploadReferences,
        event.detail?.inlineMediaPreviews,
      )
    },

    handleFocusRequest(event) {
      if (event.detail?.id !== settings.id) {
        return
      }

      this.focus()
    },

    focus() {
      if (dialectIsCurrent()) editor?.commands.focus()
    },

    extensionCommand(extension, command, ...args) {
      if (!editor || editor.isDestroyed || !dialectIsCurrent()) return false
      this.refreshEditorOptionsFromDom()
      if (!this.isEditable || !editor.isEditable
        || !extensionCommands.get(extension)?.has(command)) return false
      const parameters = copyExtensionData(args)
      const chain = editor.chain().focus()
      const result = chain[command](...parameters).run()
      if (result) this.sync()
      return result
    },

    command(name, ...args) {
      // defensive: symmetric with sync()/insertFiles()/externalUpdate() in case
      // the component was torn down before a queued command runs
      if (!editor || !dialectIsCurrent()) {
        return
      }

      if (!this.isEditable) {
        return
      }

      const chain = editor.chain().focus()

      if (typeof chain[name] !== 'function') {
        return
      }

      if (chain[name](...args).run()) {
        this.sync()
      }
    },

    canRun(name, ...args) {
      if (!editor || !this.isEditable || !dialectIsCurrent()) {
        return false
      }

      const chain = editor.can().chain().focus()

      return typeof chain[name] === 'function' && chain[name](...args).run()
    },

    refreshActive() {
      if (!editor) {
        return
      }

      this.active = {
        bold: featureEnabled('bold') && editor.isActive('bold'),
        italic: featureEnabled('italic') && editor.isActive('italic'),
        strike: featureEnabled('strike') && editor.isActive('strike'),
        underline: featureEnabled('underline') && editor.isActive('underline'),
        heading2: featureEnabled('headings') && editor.isActive('heading', { level: 2 }),
        heading3: featureEnabled('headings') && editor.isActive('heading', { level: 3 }),
        heading4: featureEnabled('headings') && editor.isActive('heading', { level: 4 }),
        bulletList: featureEnabled('bullet-list') && editor.isActive('bulletList'),
        orderedList: featureEnabled('ordered-list') && editor.isActive('orderedList'),
        blockquote: featureEnabled('blockquote') && editor.isActive('blockquote'),
        code: featureEnabled('code') && editor.isActive('code'),
        codeBlock: featureEnabled('code') && editor.isActive('codeBlock'),
        calloutInfo: featureEnabled('callouts') && editor.isActive('callout', { variant: 'info' }),
        calloutTip: featureEnabled('callouts') && editor.isActive('callout', { variant: 'tip' }),
        calloutWarning: featureEnabled('callouts') && editor.isActive('callout', { variant: 'warning' }),
        link: featureEnabled('link') && editor.isActive('link'),
      }
    },

    // INVARIANT: the hidden input (observed by wire:model) is written on blur,
    // explicit toolbar commands, form submit, or the public synchronous sync
    // API - NOT on every keystroke. onTransaction only refreshes toolbar state.
    sync() {
      const input = this.$refs.input

      // editor may already be gone if the component was torn down mid-flight
      if (!input || !editor || !dialectIsCurrent()) {
        return null
      }

      if (consumeSyncSuppression()) {
        return input.value
      }

      input.value = editor.isEmpty ? '' : editor.getHTML()
      lastWrittenInputValue = input.value
      writingInput = true

      try {
        input.dispatchEvent(new Event('input', { bubbles: true }))
      } finally {
        writingInput = false
      }

      this.$refs.editor?.dispatchEvent(new CustomEvent(SYNCED_EVENT, {
        bubbles: true,
        detail: { id: settings.id },
      }))

      return input.value
    },

    setLink() {
      if (!editor || !this.isEditable || !featureEnabled('link')) {
        return
      }

      const previous = editor.getAttributes('link').href || ''
      const url = window.prompt(settings.messages.linkPrompt || 'URL:', previous)

      if (url === null) {
        return
      }

      if (url === '') {
        this.command('unsetLink')

        return
      }

      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      this.sync()
    },

    insertEmbed() {
      if (!editor || !this.isEditable || !featureEnabled('embeds')) {
        return
      }

      const url = window.prompt(settings.messages.embedPrompt)

      if (!url) {
        return
      }

      // settings.embeds is a { key: regexSource } map built server-side from
      // every enabled provider (incl. host-registered custom ones), so custom
      // providers are insertable here too. matches() stays authoritative on
      // render.
      const provider = Object.keys(settings.embeds).find((key) => {
        try {
          return new RegExp(settings.embeds[key], 'i').test(url)
        } catch {
          return false
        }
      })

      if (!provider) {
        window.alert(settings.messages.embedInvalid)

        return
      }

      this.command('insertEmbed', url, provider)
    },

    handleFiles(files, position = null) {
      const images = Array.from(files || []).filter((file) => file.type.startsWith('image/'))

      if (images.length === 0) {
        return false
      }

      if (!this.isEditable || !featureEnabled('inline-images') || settings.inlineImages === false) {
        window.alert(settings.messages.imageRejected)

        return true
      }

      this.insertFiles(images, position)

      return true
    },

    handlePaste(event) {
      if (this.handleFiles(event.clipboardData?.files)) {
        return true
      }

      const content = embeddedImageContent(event.clipboardData)

      if (content === null) {
        return false
      }

      this.insertEmbeddedImageContent(content)

      return true
    },

    handleDrop(event, position = null) {
      if (this.handleFiles(event.dataTransfer?.files, position)) {
        return true
      }

      const content = embeddedImageContent(event.dataTransfer)

      if (content === null) {
        return false
      }

      this.insertEmbeddedImageContent(content, position)

      return true
    },

    insertEmbeddedImageContent(content, position = null) {
      if (!this.isEditable || !featureEnabled('inline-images') || settings.inlineImages === false) {
        discardUploadRecords(content.records)
        window.alert(settings.messages.imageRejected)

        return
      }

      if (!editor) {
        discardUploadRecords(content.records)

        return
      }

      uploadFailureWarned = false
      content.records.forEach((record) => uploadRecords.set(record.id, record))

      const inserted = position === null
        ? editor.chain().focus().insertContent(content.html).run()
        : editor.chain().focus().insertContentAt(position, content.html).run()

      if (!inserted) {
        discardUploadRecords(content.records)

        return
      }

      if (content.rejected) {
        window.alert(settings.messages.imageRejected)
      }

      this.sync()

      if (content.records.length > 0) {
        uploadQueue.push(...content.records)
        drainUploadQueue()
      }
    },

    async insertFiles(files, position = null) {
      if (!dialectIsCurrent() || !this.isEditable || !featureEnabled('inline-images') || settings.inlineImages === false) {
        return
      }

      const imageFiles = Array.from(files)

      if (temporaryUploadsEnabled) {
        if (imageFiles.length === 0 || !editor) {
          return
        }

        uploadFailureWarned = false

        const records = imageFiles.map((file) => createUploadRecord(file))
        const nodes = records.map((record) => ({
          type: 'image',
          attrs: {
            src: record.objectUrl,
            uploadId: record.id,
            uploadState: 'processing',
            uploadProgress: null,
            uploadToken: null,
          },
        }))

        records.forEach((record) => uploadRecords.set(record.id, record))

        const inserted = position === null
          ? editor.chain().focus().insertContent(nodes).run()
          : editor.chain().focus().insertContentAt(position, nodes).run()

        if (!inserted) {
          discardUploadRecords(records)

          return
        }

        // The binding sees only compact blob: placeholders while the serial
        // queue is active; processed data URLs stay in local function memory.
        this.sync()
        uploadQueue.push(...records)
        drainUploadQueue()
        await waitForUploads()

        return
      }

      const nodes = []
      let rejectedImage = false

      for (const file of imageFiles) {
        const dataUrl = await downscaleImage(file, settings.inlineImages)

        if (dataUrl === null || cannotFitPayloadByItself(dataUrl)) {
          rejectedImage = true
          continue
        }

        nodes.push({ type: 'image', attrs: { src: dataUrl } })
      }

      if (rejectedImage) {
        window.alert(settings.messages.imageRejected)
      }

      // downscaleImage() is async - the component (and editor) may have been
      // destroyed while we awaited, so bail instead of dereferencing null.
      if (nodes.length === 0 || !editor) {
        return
      }

      if (position === null) {
        editor.chain().focus().insertContent(nodes).run()
      } else {
        editor.chain().focus().insertContentAt(position, nodes).run()
      }

      this.sync()
    },
  }
}
